import {
    useEffect,
    useState,
    useMemo,
    useRef,
    type FC
} from "react";
import {
    type PanResponderGestureState,
    type GestureResponderEvent,
    PanResponder,
    Pressable,
    Platform,
    View
} from "react-native";
import type {
    DataTableColumnResizerKeyboardEventType,
    DataTableColumnResizerWebPropsType,
    DataTableColumnResizerProps
} from "./type";
import stylesheet, {
    DATA_TABLE_RESIZE_SHIFT_RATIO,
    DATA_TABLE_RESIZE_MAX_RATIO,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useInteractionState
} from "../../../../helpers/interaction";
import {
    usePointerType
} from "../../../../helpers/pointer";
import {
    useDataTableContext
} from "../../context";
import {
    getHorizontalStep,
    getDirectionSign
} from "../../../../helpers/direction";

const DataTableColumnResizer: FC<DataTableColumnResizerProps> = ({
    customTheme: customThemeProp,
    columnId,
    minWidth,
    isLast,
    width
}) => {
    const context = useDataTableContext({
        part: "ColumnHeader"
    });

    const {
        shapeConfigs,
        touchTargets,
        durations,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customThemeProp ?? context?.customTheme);

    const {
        direction,
        localize
    } = NCoreUIKitLocalize.useContext(context?.customLocalize);

    const pointerType = usePointerType();

    const {
        interactionHandlers,
        isFocusVisible
    } = useInteractionState();

    const [
        isDragging,
        setIsDragging
    ] = useState(false);

    const startWidthRef = useRef(0);

    const lastTapRef = useRef(0);

    const pageLockRef = useRef< |
    {
        userSelect: string;
        cursor: string;
    } |
    undefined>(undefined);

    const latestRef = useRef({
        handleColumnWidthChange: context?.handleColumnWidthChange,
        direction,
        minWidth,
        columnId,
        width
    });

    latestRef.current = {
        handleColumnWidthChange: context?.handleColumnWidthChange,
        direction,
        minWidth,
        columnId,
        width
    };

    const handleUnlockPage = () => {
        const lock = pageLockRef.current;

        const body = Platform.OS === "web" ? globalThis.document?.body : undefined;

        pageLockRef.current = undefined;

        if(!lock || !body) {
            return;
        }

        body.style.cursor = lock.cursor;

        body.style.userSelect = lock.userSelect;
    };

    const handleLockPage = () => {
        const body = Platform.OS === "web" ? globalThis.document?.body : undefined;

        if(!body) {
            return;
        }

        pageLockRef.current = {
            userSelect: body.style.userSelect,
            cursor: body.style.cursor
        };

        body.style.cursor = "col-resize";

        body.style.userSelect = "none";
    };

    const panResponder = useMemo(() => {
        const handleFinish = (gestureState: PanResponderGestureState, isRelease: boolean) => {
            handleUnlockPage();

            setIsDragging(false);

            const isTap = isRelease && Math.abs(gestureState.dx) + Math.abs(gestureState.dy) < shapeConfigs.tapSlop;

            if(!isTap) {
                lastTapRef.current = 0;

                return;
            }

            const now = Date.now();

            if(now - lastTapRef.current < durations.doubleTap) {
                lastTapRef.current = 0;

                latestRef.current.handleColumnWidthChange?.(latestRef.current.columnId, undefined);

                return;
            }

            lastTapRef.current = now;
        };

        return PanResponder.create({
            onPanResponderTerminate: (_event: GestureResponderEvent, gestureState: PanResponderGestureState) => handleFinish(gestureState, false),
            onPanResponderRelease: (_event: GestureResponderEvent, gestureState: PanResponderGestureState) => handleFinish(gestureState, true),
            onPanResponderMove: (_event: GestureResponderEvent, gestureState: PanResponderGestureState) => {
                const {
                    direction: currentDirection,
                    columnId: currentColumnId,
                    minWidth: currentMinWidth,
                    handleColumnWidthChange
                } = latestRef.current;

                const nextWidth = startWidthRef.current + gestureState.dx * getDirectionSign({
                    direction: currentDirection
                });

                handleColumnWidthChange?.(currentColumnId, Math.max(currentMinWidth, Math.round(nextWidth)));
            },
            onPanResponderGrant: () => {
                startWidthRef.current = latestRef.current.width;

                handleLockPage();

                setIsDragging(true);
            },
            onPanResponderTerminationRequest: () => false,
            onMoveShouldSetPanResponder: () => true,
            onStartShouldSetPanResponder: () => true
        });
    }, [
        shapeConfigs.tapSlop,
        durations.doubleTap
    ]);

    useEffect(() => {
        return () => {
            handleUnlockPage();
        };
    }, []);

    const hit = touchTargets.handle;

    const {
        container: containerDynamicStyle,
        line: lineDynamicStyle
    } = useStyles({
        isFocusVisible,
        shapeConfigs,
        isDragging,
        borders,
        colors,
        isLast,
        hit
    });

    if(!context || pointerType === "coarse" || Platform.OS !== "web") {
        return null;
    }

    const column = context.columns.find((item) => item.id === columnId);

    const onKeyDown = (event: DataTableColumnResizerKeyboardEventType) => {
        if(event.target !== event.currentTarget || event.altKey || event.ctrlKey || event.metaKey) {
            return;
        }

        if(event.key === "Enter") {
            event.preventDefault();

            context.handleColumnWidthChange(columnId, undefined);

            return;
        }

        if(event.key === "Home") {
            event.preventDefault();

            context.handleColumnWidthChange(columnId, minWidth);

            return;
        }

        const step = getHorizontalStep({
            key: event.key,
            direction
        });

        if(step === 0) {
            return;
        }

        event.preventDefault();

        context.handleColumnWidthChange(columnId, Math.max(minWidth, Math.round(width + step * spaces.spacingSm * (event.shiftKey ? DATA_TABLE_RESIZE_SHIFT_RATIO : 1))));
    };

    const webProps: DataTableColumnResizerWebPropsType = {
        "aria-orientation": "vertical",
        onKeyDown
    };

    return <Pressable
        {...interactionHandlers}
        {...webProps}
        aria-valuemax={minWidth * DATA_TABLE_RESIZE_MAX_RATIO}
        aria-label={localize("data-table-resize-column", [
            column?.header ?? columnId
        ])}
        style={[
            stylesheet.container,
            containerDynamicStyle
        ]}
        aria-valuemin={minWidth}
        aria-valuenow={width}
        role="separator"
        focusable={true}
    >
        <View
            style={[
                stylesheet.line,
                lineDynamicStyle
            ]}
        />
        <View
            {...panResponder.panHandlers}
            style={stylesheet.pan}
        />
    </Pressable>;
};
export default DataTableColumnResizer;
